import { createAsyncThunk, createSelector,createSlice } from '@reduxjs/toolkit'
import { RootState } from 'store'

import AiLimitsRepository, {
  IAiLimitsResponse
} from 'services/repositories/AiLimitsRepository'
import { TLoadingStatus } from 'types/user'

interface IAiLimitsState {
  userLimits: IAiLimitsResponse | null
  brandCountryLimits: IAiLimitsResponse | null
  globalLimits: IAiLimitsResponse | null
  status: TLoadingStatus
  error: string | null
}

const initialState: IAiLimitsState = {
  userLimits: null,
  brandCountryLimits: null,
  globalLimits: null,
  status: 'initial',
  error: null
}

export const types = {
  getUserLimits: 'aiLimits/getUserLimits',
  getBrandCountryLimits: 'aiLimits/getBrandCountryLimits',
  getGlobalLimits: 'aiLimits/getGlobalLimits'
} as const

export const getUserLimits = createAsyncThunk(
  types.getUserLimits,
  async (params: { userId: string }, { rejectWithValue }) => {
    try {
      const response = await AiLimitsRepository.getAiLimits({
        operationType: 'translation',
        userId: params.userId
      })

      return response.data
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || 'Failed to fetch user limits'
      )
    }
  }
)

export const getBrandCountryLimits = createAsyncThunk(
  types.getBrandCountryLimits,
  async (
    params: { userId: string; brandId: string; countryCode: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await AiLimitsRepository.getAiLimits({
        operationType: 'translation',
        userId: params.userId,
        brandId: params.brandId,
        countryCode: params.countryCode
      })

      return response.data
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || 'Failed to fetch brand country limits'
      )
    }
  }
)

export const getGlobalLimits = createAsyncThunk(
  types.getGlobalLimits,
  async (_, { rejectWithValue }) => {
    try {
      const response = await AiLimitsRepository.getAiLimits({
        operationType: 'translation'
      })

      return response.data
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || 'Failed to fetch global limits'
      )
    }
  }
)

export const aiLimitsSlice = createSlice({
  name: 'aiLimits',
  initialState,
  reducers: {
    clearLimits: state => {
      state.userLimits = null
      state.brandCountryLimits = null
      state.globalLimits = null
      state.error = null
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getUserLimits.pending, state => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(getUserLimits.fulfilled, (state, action) => {
        state.status = 'loaded'
        state.userLimits = action.payload
        state.error = null
      })
      .addCase(getUserLimits.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload as string
      })
      .addCase(getBrandCountryLimits.pending, state => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(getBrandCountryLimits.fulfilled, (state, action) => {
        state.status = 'loaded'
        state.brandCountryLimits = action.payload
        state.error = null
      })
      .addCase(getBrandCountryLimits.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload as string
      })
      .addCase(getGlobalLimits.pending, state => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(getGlobalLimits.fulfilled, (state, action) => {
        state.status = 'loaded'
        state.globalLimits = action.payload
        state.error = null
      })
      .addCase(getGlobalLimits.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload as string
      })
  }
})

export const { clearLimits } = aiLimitsSlice.actions

export const selectAiLimits = (state: RootState) => state.aiLimits

export const selectUserLimits = createSelector(
  selectAiLimits,
  aiLimits => aiLimits.userLimits
)

export const selectBrandCountryLimits = createSelector(
  selectAiLimits,
  aiLimits => aiLimits.brandCountryLimits
)

export const selectGlobalLimits = createSelector(
  selectAiLimits,
  aiLimits => aiLimits.globalLimits
)

export const selectAiLimitsStatus = createSelector(
  selectAiLimits,
  aiLimits => aiLimits.status
)

export const selectAiLimitsError = createSelector(
  selectAiLimits,
  aiLimits => aiLimits.error
)

export const selectDisplayLimits = createSelector(
  [selectUserLimits, selectBrandCountryLimits, selectGlobalLimits],
  (userLimits, brandCountryLimits, globalLimits) => {
    if (
      userLimits?.hasLimit &&
      userLimits.remaining &&
      userLimits.remaining > 0
    ) {
      return { limits: userLimits, type: 'user' as const }
    }

    if (
      brandCountryLimits?.hasLimit &&
      brandCountryLimits.remaining &&
      brandCountryLimits.remaining > 0
    ) {
      return { limits: brandCountryLimits, type: 'brand_country' as const }
    }

    if (
      globalLimits?.hasLimit &&
      globalLimits.remaining &&
      globalLimits.remaining > 0
    ) {
      return { limits: globalLimits, type: 'global' as const }
    }

    if (userLimits?.hasLimit) {
      return { limits: userLimits, type: 'user' as const }
    }

    if (brandCountryLimits?.hasLimit) {
      return { limits: brandCountryLimits, type: 'brand_country' as const }
    }

    if (globalLimits?.hasLimit) {
      return { limits: globalLimits, type: 'global' as const }
    }

    return null
  }
)

export default aiLimitsSlice.reducer
